import React, {
    HTMLAttributes, ReactElement, ReactNode, useContext,
} from "react";
import Styles from "./Settings.module.scss";
import Popover from "../../popover/Popover";
import { Events, SettingsContainerContext, SettingsSectionContext } from "./base";

export interface SettingsSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
    children?: ReactNode;
    sectionKey?: string;
    title: ReactNode;
    counter?: string;
    helpText?: string;
    shouldDisplayTitle?: boolean;
    subtitle?: ReactNode;
}

const SettingsSection = React.forwardRef((
    args: SettingsSectionProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const {
        children,
        title,
        helpText,
        sectionKey,
        shouldDisplayTitle,
        subtitle,
        counter,
        ...rest
    } = args;

    const { section, reference } = useContext(SettingsContainerContext).useSection(ref, title, helpText, sectionKey, counter);
    section.container.settingsRegistry.events.useEvent(Events.SETTING_CHANGED, Events.SECTION_LOADED);

    return (
        <SettingsSectionContext.Provider value={section}>
            {section.canDisplay ? (
                <div className={Styles.section} ref={reference} {...rest}>
                    {shouldDisplayTitle && title && (
                        <h2 className={Styles["section-title"]} ref={ref} {...rest}>
                            {title}
                            {subtitle && <small className={Styles["settings-subtitle"]}>{subtitle}</small>}

                            {helpText && (
                                <Popover
                                    hasArrow
                                    size="md"
                                    variant="tooltip"
                                    iconSize="sm"
                                    popoverClassName={Styles["setting-title-help"]}
                                    buttonClassName={Styles["setting-title-help-button"]}
                                >
                                    {helpText}
                                </Popover>
                            )}
                        </h2>
                    )}

                    {children}
                </div>
            ) : (
                <div className={Styles.invisible}>{children}</div>
            )}
        </SettingsSectionContext.Provider>
    );
});

SettingsSection.defaultProps = {
    shouldDisplayTitle: true,
    subtitle: undefined,
    counter: undefined,
};

export default SettingsSection;
